<template>
  <section class="py-16 md:py-24 bg-white/30">
    <div class="container">
      <h2 class="text-3xl md:text-4xl font-bold text-center mb-12 text-dark">
        {{ $t("get_in_touch") }}
      </h2>
      <div class="grid sm:grid-cols-2 md:grid-cols-3 gap-8 md:gap-10">
        <ContactInfo
          :title="$t('landing.contact_info.dubai_head_office')"
          class="bg-white/40 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
        >
          <div class="flex items-start gap-4">
            <svg
              class="w-6 h-6 text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 13.43C13.7231 13.43 15.12 12.0331 15.12 10.31C15.12 8.58687 13.7231 7.19 12 7.19C10.2769 7.19 8.88 8.58687 8.88 10.31C8.88 12.0331 10.2769 13.43 12 13.43Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
              <path
                d="M3.62001 8.49C5.59001 -0.169998 18.42 -0.159998 20.38 8.5C21.53 13.58 18.37 17.88 15.6 20.54C13.59 22.48 10.41 22.48 8.39001 20.54C5.63001 17.88 2.47001 13.57 3.62001 8.49Z"
                stroke="currentColor"
                stroke-width="1.5"
              />
            </svg>
            <a
              href="https://www.google.com/maps?q=25.18822,55.25836"
              target="_blank"
              class="text-gray-700 hover:text-green-700 font-medium font-openSans transition-colors"
            >
              {{ $t("landing.contact_info.dubai_head_office_address") }}
            </a>
          </div>
        </ContactInfo>
        <ContactInfo
          :title="$t('landing.contacts.phone_number')"
          class="bg-white/40 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
        >
          <div class="flex items-start gap-4">
            <svg
              class="w-6 h-6 text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.97 18.33C21.97 18.69 21.89 19.06 21.72 19.42C21.55 19.78 21.33 20.12 21.04 20.44C20.55 20.98 20.01 21.37 19.4 21.62C18.8 21.87 18.15 22 17.45 22C16.43 22 15.34 21.76 14.19 21.27C13.04 20.78 11.89 20.12 10.75 19.29C9.6 18.45 8.51 17.52 7.47 16.49C6.44 15.45 5.51 14.36 4.68 13.22C3.86 12.08 3.2 10.94 2.72 9.81C2.24 8.67 2 7.58 2 6.54C2 5.86 2.12 5.21 2.36 4.61C2.6 4 2.98 3.44 3.51 2.94C4.15 2.31 4.85 2 5.59 2C5.87 2 6.15 2.06 6.4 2.18C6.66 2.3 6.89 2.48 7.07 2.74L9.39 6.01C9.57 6.26 9.7 6.49 9.79 6.71C9.88 6.92 9.93 7.13 9.93 7.32C9.93 7.56 9.86 7.8 9.72 8.03C9.59 8.26 9.4 8.5 9.16 8.74L8.4 9.53C8.29 9.64 8.24 9.77 8.24 9.93C8.24 10.01 8.25 10.08 8.27 10.16C8.3 10.24 8.33 10.3 8.35 10.36C8.53 10.69 8.84 11.12 9.28 11.64C9.73 12.16 10.21 12.69 10.73 13.22C11.27 13.75 11.79 14.24 12.32 14.69C12.84 15.13 13.27 15.43 13.61 15.61C13.66 15.63 13.72 15.66 13.79 15.69C13.87 15.72 13.95 15.73 14.04 15.73C14.21 15.73 14.34 15.67 14.45 15.56L15.21 14.81C15.46 14.56 15.7 14.37 15.93 14.25C16.16 14.11 16.39 14.04 16.64 14.04C16.83 14.04 17.03 14.08 17.25 14.17C17.47 14.26 17.7 14.39 17.95 14.56L21.26 16.91C21.52 17.09 21.7 17.31 21.81 17.55C21.91 17.8 21.97 18.05 21.97 18.33Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
              />
            </svg>
            <a
              href="tel:+971543571111"
              target="_blank"
              class="text-gray-700 hover:text-green-700 font-medium font-openSans transition-colors direction-ltr"
            >
              <span class="rtl:hidden">+</span>971-54-357-1111<span
                class="rtl:inline-block"
                >+</span
              >
            </a>
          </div>
        </ContactInfo>
        <ContactInfo
          :title="$t('landing.contacts.email')"
          class="bg-white/40 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
        >
          <div class="flex items-start gap-4">
            <svg
              class="w-6 h-6 text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 20.5H7C4 20.5 2 19 2 15.5V8.5C2 5 4 3.5 7 3.5H17C20 3.5 22 5 22 8.5V15.5C22 19 20 20.5 17 20.5Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M17 9L13.87 11.5C12.84 12.32 11.15 12.32 10.12 11.5L7 9"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <a
              href="#"
              class="text-gray-700 hover:text-green-700 font-medium font-openSans transition-colors"
            >
              support@sarafrobot.com
            </a>
          </div>
        </ContactInfo>
        <ContactInfo
          :title="$t('landing.contacts.support_service')"
          class="bg-white/40 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
        >
          <div class="flex items-start gap-4">
            <svg
              class="w-6 h-6 text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.5 19H8C4 19 2 18 2 13V8C2 4 4 2 8 2H16C20 2 22 4 22 8V13C22 17 20 19 16 19H15.5C15.19 19 14.89 19.15 14.7 19.4L13.2 21.4C12.54 22.28 11.46 22.28 10.8 21.4L9.3 19.4C9.14 19.18 8.77 19 8.5 19Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-miterlimit="10"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 8H17"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 13H13"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div>
              <p class="text-gray-600 mb-2">
                {{ $t("landing.contacts.live_chat") }}
              </p>
              <a
                href="https://t.me/sarafrobot_support"
                target="_blank"
                class="text-gray-700 hover:text-green-700 font-medium font-openSans transition-colors"
              >
                @sarafrobot_support
              </a>
            </div>
          </div>
        </ContactInfo>
        <ContactInfo
          :title="$t('landing.contacts.partnerships')"
          class="bg-white/40 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
        >
          <div class="flex items-start gap-4">
            <svg
              class="w-6 h-6 text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M8 14C8 14 9.5 16 12 16C14.5 16 16 14 16 14"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M9 9H9.01"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M15 9H15.01"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <a
              href="#"
              class="text-gray-700 hover:text-green-700 font-medium font-openSans transition-colors"
            >
              business@sarafrobot.com
            </a>
          </div>
        </ContactInfo>
        <ContactInfo
          :title="$t('landing.contacts.community')"
          class="bg-white/40 backdrop-blur-sm p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300"
        >
          <div class="flex items-start gap-4">
            <svg
              class="w-6 h-6 text-green-700"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 8C18.6569 8 20 6.65685 20 5C20 3.34315 18.6569 2 17 2C15.3431 2 14 3.34315 14 5C14 6.65685 15.3431 8 17 8Z"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 13H12"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M7 17H16"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M14 2H9C4 2 2 4 2 9V15C2 20 4 22 9 22H15C20 22 22 20 22 15V10"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            <div>
              <p class="text-gray-600 mb-2">
                {{ $t("landing.contacts.join_community") }}
              </p>
              <a
                href="https://t.me/sarafrobotcommunity"
                target="_blank"
                class="text-gray-700 hover:text-green-700 font-medium font-openSans transition-colors direction-ltr"
              >
                @sarafrobotcommunity
              </a>
            </div>
          </div>
        </ContactInfo>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import ContactInfo from "@/modules/Landing/components/Card/ContactInfo.vue";
</script>

<style scoped></style>
